import { ref, get, child } from "firebase/database";
import { dbAsistencia } from "../firebase";
import { fetchAsistenciaByMonth, parseDMY, STATUS } from "./asistenciaCampoService";

const DAY_MS = 24 * 60 * 60 * 1000;

// Orden lunes→domingo; Date#getDay() devuelve 0 para domingo.
const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const ANTIGUEDAD_BUCKETS = [
  { key: "reciente", label: "Hasta 2 días", maxDias: 2 },
  { key: "semana", label: "3 a 7 días", maxDias: 7 },
  { key: "quincena", label: "8 a 15 días", maxDias: 15 },
  { key: "atrasada", label: "Más de 15 días", maxDias: Infinity },
];

function getMonth(date) {
  return date instanceof Date ? date.getMonth() + 1 : null;
}

function getYear(date) {
  return date instanceof Date ? date.getFullYear() : null;
}

function resolveDashboardDate(row) {
  if (row?.desde instanceof Date && !Number.isNaN(row.desde.getTime())) {
    return row.desde;
  }
  const applied = parseYmdLocal(row?.appliedDate);
  if (applied) return applied;
  if (row?.hasta instanceof Date && !Number.isNaN(row.hasta.getTime())) {
    return row.hasta;
  }
  return null;
}

function parseYmdLocal(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;

  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  const d = new Date(year, month, day, 0, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(date) {
  if (!(date instanceof Date)) return null;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  );
}

function endOfDay(date) {
  if (!(date instanceof Date)) return null;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );
}

function hasDestinoByPattern(messageRaw) {
  const text = String(messageRaw || "").replace(/\s+/g, " ").trim();
  if (!text) return false;

  const m =
    /desde\s*\d{1,2}:\d{1,2}\s*hasta\s*(?:\d{1,2}:\d{1,2}|--:--)\s*[-:]\s*(.+)$/i.exec(
      text
    );
  if (!m) return false;

  const destino = String(m[1] || "").replace(/\s+/g, " ").trim().toLowerCase();
  if (!destino) return false;

  const invalid = new Set([
    "none",
    "ninguna",
    "ninguno",
    "s/d",
    "sin destino",
    "no especifica",
    "no especificado",
    "-",
  ]);

  return !invalid.has(destino);
}

function hasEndTimeInJustificacion(messageRaw) {
  const text = String(messageRaw || "").replace(/\s+/g, " ").trim();
  if (!text) return false;
  const m = /hasta\s*((?:[0-9]{1,2}|--)\s*:\s*(?:[0-9]{1,2}|--))/i.exec(text);
  if (!m?.[1]) return false;
  const token = String(m[1]).replace(/\s+/g, "");
  return token !== "--:--";
}

function extractDestinoFromJustificacion(messageRaw) {
  const text = String(messageRaw || "").replace(/\s+/g, " ").trim();
  if (!text) return null;

  const m =
    /desde\s*\d{1,2}:\d{1,2}\s*hasta\s*(?:\d{1,2}:\d{1,2}|--:--)\s*[-:]\s*(.+)$/i.exec(
      text
    );
  if (!m) return null;

  const destinoRaw = String(m[1] || "").replace(/\s+/g, " ").trim();
  if (!destinoRaw) return null;

  const destinoNorm = destinoRaw.toLowerCase();
  const invalid = new Set([
    "none",
    "ninguna",
    "ninguno",
    "s/d",
    "sin destino",
    "no especifica",
    "no especificado",
    "-",
  ]);
  if (invalid.has(destinoNorm)) return null;

  return destinoRaw;
}

function titleCase(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

const DESTINO_CATALOG = [
  { label: "Gobierno Regional Cusco", patterns: ["gobierno regional cusco", "gore cusco", "gobierno regional"] },
  { label: "UNSAAC", patterns: ["unsaac", "universidad nacional de san antonio abad del cusco"] },
  { label: "Cusipata / Pampachulla", patterns: ["cusipata", "pampachulla"] },
  { label: "Quiquijana / Hayuni", patterns: ["quiquijana", "hayuni"] },
  { label: "Lucre / Huayllarpampa", patterns: ["lucre", "huayllarpampa"] },
  { label: "Saylla", patterns: ["saylla"] },
  { label: "Andahuaylillas", patterns: ["andahuaylillas"] },
  { label: "Oropesa / Vicho / Patabamba", patterns: ["oropesa", "vicho", "patabamba"] },
  { label: "Municipalidad de Chinchero", patterns: ["municipalidad de chinchero", "reunion funcionarios municipalidad de chinchero"] },
  { label: "Poroy / Tikahuerta", patterns: ["poroy", "huarahuaylla", "tikahuerta"] },
  { label: "San Jeronimo / Usphabamba", patterns: ["san jeronimo", "usphabamba"] },
  { label: "Urcos", patterns: ["urcos"] },
  { label: "Huaro", patterns: ["huaro"] },
  { label: "Ccorca / Cusibamba", patterns: ["ccorca", "cusibamba"] },
  { label: "Santiago / Occopata", patterns: ["santiago", "occopata"] },
  { label: "Cachimayo", patterns: ["cachimayo"] },
  { label: "Chinchero / Taucca", patterns: ["chinchero", "taucca"] },
  { label: "Wanchaq", patterns: ["wanchaq"] },
  { label: "Anta / Occoruro", patterns: ["anta", "occoruro"] },
  { label: "Mollepata", patterns: ["mollepata"] },
  { label: "San Sebastian", patterns: ["san sebastian"] },
  { label: "Calca / Lamay", patterns: ["calca", "lamay"] },
  { label: "Lima", patterns: ["lima"] },
];

function detectGeoLabels(rawText) {
  const norm = normalizeText(rawText);
  if (!norm) return [];

  const matches = [];

  DESTINO_CATALOG.forEach((item) => {
    const found = item.patterns.some((p) => norm.includes(normalizeText(p)));
    if (found) matches.push(item.label);
  });

  const hasSpecificDistrictMatch = matches.length > 0;
  const hasMunicipalKeyword = /\bmunicipalidad(?:es)?\b|\bmunicipio\b/i.test(norm);

  // Solo usar la etiqueta genérica cuando no hubo un distrito/ubicación más específica.
  if (!hasSpecificDistrictMatch && hasMunicipalKeyword) {
    matches.push("Municipalidades (sin distrito)");
  }

  return Array.from(new Set(matches));
}

function pickPrimaryGeoLabel(rawText) {
  const norm = normalizeText(rawText);
  if (!norm) return null;

  const priorityCatalog = [
    { label: "Gobierno Regional Cusco", patterns: ["gobierno regional cusco", "gore cusco", "gobierno regional"] },
    { label: "UNSAAC", patterns: ["unsaac", "universidad nacional de san antonio abad del cusco"] },
    { label: "Cusco", patterns: [" cusco", "cuzco"] },
    { label: "Cusipata / Pampachulla", patterns: ["cusipata", "pampachulla"] },
    { label: "Quiquijana / Hayuni", patterns: ["quiquijana", "hayuni"] },
    { label: "Lucre / Huayllarpampa", patterns: ["lucre", "huayllarpampa"] },
    { label: "Saylla", patterns: ["saylla"] },
    { label: "Andahuaylillas", patterns: ["andahuaylillas"] },
    { label: "Oropesa / Vicho / Patabamba", patterns: ["oropesa", "vicho", "patabamba"] },
    { label: "Municipalidad de Chinchero", patterns: ["municipalidad de chinchero", "reunion funcionarios municipalidad de chinchero"] },
    { label: "Poroy / Tikahuerta", patterns: ["poroy", "tikahuerta"] },
    { label: "San Jeronimo / Usphabamba", patterns: ["san jeronimo", "usphabamba"] },
    { label: "Urcos", patterns: ["urcos"] },
    { label: "Huaro", patterns: ["huaro"] },
    { label: "Ccorca / Cusibamba", patterns: ["ccorca", "cusibamba"] },
    { label: "Santiago / Occopata", patterns: ["santiago", "occopata"] },
    { label: "Cachimayo", patterns: ["cachimayo"] },
    { label: "Chinchero / Taucca", patterns: ["chinchero", "taucca"] },
    { label: "Wanchaq", patterns: ["wanchaq"] },
    { label: "Anta / Occoruro", patterns: ["anta", "occoruro"] },
    { label: "Mollepata", patterns: ["mollepata"] },
    { label: "San Sebastian", patterns: ["san sebastian"] },
    { label: "Calca / Lamay", patterns: ["calca", "lamay"] },
    { label: "Lima", patterns: ["lima"] },
  ];

  for (const item of priorityCatalog) {
    if (item.patterns.some((p) => norm.includes(normalizeText(p)))) {
      return item.label;
    }
  }

  if (/\bmunicipalidad(?:es)?\b|\bmunicipio\b/i.test(norm)) {
    return "Municipalidades (sin distrito)";
  }

  return null;
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

const TYPE_LABELS = {
  op: "No Pude Picar",
  va: "Vacaciones",
  sc: "Comisión de servicio",
  pe: "Permiso ocasional",
  pa: "Permiso personal",
  tc: "Trabajo Corrido",
  vi: "Viaje",
};

function normalizeTipoPermiso(typeRaw) {
  const t = normalizeText(typeRaw);
  return TYPE_LABELS[t] || String(typeRaw || "Sin tipo").trim();
}

function isOlvidePicarRow(row) {
  const tipoKey = normalizeText(row?.tipoKey);
  if (tipoKey === "op") return true;
  return normalizeText(row?.tipoPermiso) === "olvide picar";
}

export async function fetchDashboardData({
  role,
  managerCode,
  monthPermisos = null,
  yearPermisos = null,
  fromDatePermisos = null,
  toDatePermisos = null,
} = {}) {
  const root = ref(dbAsistencia);

  const usersSnap = await get(child(root, "users"));
  const users = usersSnap.exists() ? usersSnap.val() : {};

  const activeUsersAll = Object.entries(users)
    .filter(([, u]) => u?.active !== false)
    .map(([uid, u]) => ({
      uid,
      managerCode: u.managerCode || u.manager || null,
    }));

  const scopedUsers =
    role === "manager" && managerCode
      ? activeUsersAll.filter((u) => u.managerCode === managerCode)
      : activeUsersAll;

  const totalUsers = scopedUsers.length;
  const activeUsers = scopedUsers.length;

  let asistenciaCompleta = 0;
  let asistenciaIncompleta = 0;
  let sinMarcacion = activeUsers;
  let cumplimientoPct = 0;

  const leavesAll = await fetchAsistenciaByMonth(null);

  const leavesScoped =
    role === "manager" && managerCode
      ? leavesAll.filter((r) => (r.managerCode || r.manager || null) === managerCode)
      : leavesAll;

  const fromDay = parseYmdLocal(fromDatePermisos);
  const toDay = parseYmdLocal(toDatePermisos);
  const rangeStart = fromDay ? startOfDay(fromDay) : null;
  const rangeEnd = toDay ? endOfDay(toDay) : null;
  const hasRangeFilter = Boolean(rangeStart || rangeEnd);

  const leavesForBar = leavesScoped.filter((r) => {
    const rowDate = resolveDashboardDate(r);
    if (!(rowDate instanceof Date)) return false;
    // Excluir retirados de todas las métricas del dashboard
    if (Number(r.withdrawalStatus ?? 0) === 1) return false;

    if (hasRangeFilter) {
      const t = rowDate.getTime();
      if (rangeStart && t < rangeStart.getTime()) return false;
      if (rangeEnd && t > rangeEnd.getTime()) return false;
      return true;
    }

    const m = getMonth(rowDate);
    const y = getYear(rowDate);

    if (yearPermisos && y !== yearPermisos) return false;
    if (monthPermisos && m !== monthPermisos) return false;
    return true;
  });

  const pendientes = leavesForBar.filter((r) => r.estado === STATUS.PENDING).length;
  const aprobados = leavesForBar.filter((r) => r.estado === STATUS.APPROVED).length;
  const rechazados = leavesForBar.filter((r) => r.estado === STATUS.DENIED).length;

  const tipoCounts = {};
  leavesForBar.forEach((r) => {
    const tipo = normalizeTipoPermiso(r.tipoPermiso);
    if (!tipoCounts[tipo]) {
      tipoCounts[tipo] = { total: 0, aprobadas: 0, pendientes: 0, rechazadas: 0 };
    }
    const counts = tipoCounts[tipo];
    counts.total += 1;
    const estado = r.estado || STATUS.PENDING;
    if (estado === STATUS.APPROVED) counts.aprobadas += 1;
    else if (estado === STATUS.DENIED) counts.rechazadas += 1;
    else counts.pendientes += 1;
  });

  const tiposOrdenados = Object.entries(tipoCounts)
    .map(([tipo, counts]) => ({ tipo, ...counts }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 8);

  const rankingTiposSalida = tiposOrdenados.map(({ tipo, total }) => ({ tipo, total }));
  const estadoPorTipo = tiposOrdenados.map(({ tipo, aprobadas, pendientes, rechazadas }) => ({
    tipo,
    aprobadas,
    pendientes,
    rechazadas,
  }));

  const userCounts = {};
  leavesForBar.forEach((r) => {
    const user = String(r.usuario || r.userId || "Sin nombre").trim();
    if (!userCounts[user]) {
      userCounts[user] = { total: 0, olvidePicar: 0, otras: 0 };
    }
    userCounts[user].total += 1;
    if (isOlvidePicarRow(r)) userCounts[user].olvidePicar += 1;
    else userCounts[user].otras += 1;
  });

  const rankingUsuariosSalida = Object.entries(userCounts)
    .map(([usuario, counts]) => ({
      usuario,
      total: counts.total,
      olvidePicar: counts.olvidePicar,
      otras: counts.otras,
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);

  // Solicitudes que recibió cada responsable (aprobador del equipo), por estado.
  const responsableCounts = {};
  leavesForBar.forEach((r) => {
    const responsable = String(r.responsable || "Sin responsable").trim();
    if (!responsableCounts[responsable]) {
      responsableCounts[responsable] = { total: 0, aprobadas: 0, pendientes: 0, rechazadas: 0 };
    }
    const counts = responsableCounts[responsable];
    counts.total += 1;
    const estado = r.estado || STATUS.PENDING;
    if (estado === STATUS.APPROVED) counts.aprobadas += 1;
    else if (estado === STATUS.DENIED) counts.rechazadas += 1;
    else counts.pendientes += 1;
  });
  const solicitudesPorResponsable = Object.entries(responsableCounts)
    .map(([responsable, counts]) => ({ responsable, ...counts }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);

  // Comisiones de servicio no rechazadas: ¿registraron hora de fin?
  const calidadRows = leavesForBar.filter(
    (r) => String(r?.tipoKey || "").toLowerCase() === "sc" && r.estado !== STATUS.DENIED
  );
  const calidadRegistroCampo = {
    total: calidadRows.length,
    conHoraFin: calidadRows.filter((r) => hasEndTimeInJustificacion(r?.justificacion)).length,
  };

  const salidasPorDiaSemana = DIAS_SEMANA.map((dia) => ({ dia, campo: 0, otros: 0 }));
  leavesForBar.forEach((r) => {
    const rowDate = resolveDashboardDate(r);
    if (!(rowDate instanceof Date)) return;
    const idx = (rowDate.getDay() + 6) % 7;
    if (String(r?.tipoKey || "").toLowerCase() === "sc") salidasPorDiaSemana[idx].campo += 1;
    else salidasPorDiaSemana[idx].otros += 1;
  });

  const campoRows = leavesForBar.filter(
    (r) => String(r?.tipoKey || "").toLowerCase() === "sc"
      && r.estado === STATUS.APPROVED
      && Number(r.withdrawalStatus ?? 0) !== 1
  );
  const campoTotalSolicitudes = campoRows.length;

  const campoConDestino = campoRows.filter((r) =>
    hasDestinoByPattern(r?.justificacion)
  ).length;

  const campoSinDestino = Math.max(0, campoTotalSolicitudes - campoConDestino);

  const destinoCounts = {};
  campoRows.forEach((r) => {
    const justificacion = String(r?.justificacion || "");
    const labels = detectGeoLabels(justificacion);
    if (!labels.length) {
      const label = pickPrimaryGeoLabel(justificacion);
      if (label) labels.push(label);
    }
    labels.forEach((label) => {
      destinoCounts[label] = (destinoCounts[label] || 0) + 1;
    });
  });

  const rankingDestinosCriticos = Object.entries(destinoCounts)
    .map(([destino, total]) => ({ destino, total }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 20);

  const campoPendientes = campoRows.filter(
    (r) => (r.estado || STATUS.PENDING) === STATUS.PENDING
  ).length;
  const campoAprobadas = campoRows.filter(
    (r) => (r.estado || STATUS.PENDING) === STATUS.APPROVED
  ).length;
  const campoRechazadas = campoRows.filter(
    (r) => (r.estado || STATUS.PENDING) === STATUS.DENIED
  ).length;

  const campoDestinoPct =
    campoTotalSolicitudes > 0
      ? Number(((campoConDestino / campoTotalSolicitudes) * 100).toFixed(1))
      : 0;

  const campoRowsPendientes = leavesForBar.filter(
    (r) =>
      String(r?.tipoKey || "").toLowerCase() === "sc" &&
      (r.estado || STATUS.PENDING) === STATUS.PENDING &&
      Number(r.withdrawalStatus ?? 0) !== 1
  );
  const campoPendientesTotal = campoRowsPendientes.length;
  const campoConHoraFin = campoRowsPendientes.filter((r) =>
    hasEndTimeInJustificacion(r?.justificacion)
  ).length;
  const campoSinHoraFin = Math.max(0, campoPendientesTotal - campoConHoraFin);
  const campoHoraFinPct =
    campoPendientesTotal > 0
      ? Number(((campoConHoraFin / campoPendientesTotal) * 100).toFixed(1))
      : 0;

  // Días que llevan esperando decisión las pendientes del período (desde que se solicitaron).
  const hoy = startOfDay(new Date());
  const antiguedadCounts = Object.fromEntries(ANTIGUEDAD_BUCKETS.map((b) => [b.key, 0]));
  let pendienteMasAntiguaDias = 0;
  leavesForBar.forEach((r) => {
    if ((r.estado || STATUS.PENDING) !== STATUS.PENDING) return;
    const solicitado = parseDMY(r?.appliedDate) || resolveDashboardDate(r);
    if (!(solicitado instanceof Date)) return;
    const dias = Math.max(0, Math.round((hoy.getTime() - startOfDay(solicitado).getTime()) / DAY_MS));
    const bucket = ANTIGUEDAD_BUCKETS.find((b) => dias <= b.maxDias);
    antiguedadCounts[bucket.key] += 1;
    pendienteMasAntiguaDias = Math.max(pendienteMasAntiguaDias, dias);
  });
  const antiguedadPendientes = ANTIGUEDAD_BUCKETS.map((b) => ({
    key: b.key,
    label: b.label,
    total: antiguedadCounts[b.key],
  }));

  return {
    totalUsers,
    activeUsers,
    asistenciaCompleta,
    asistenciaIncompleta,
    sinMarcacion,
    cumplimientoPct,
    pendientes,
    aprobados,
    rechazados,
    estadoPorTipo,
    rankingTiposSalida,
    rankingUsuariosSalida,
    solicitudesPorResponsable,
    rankingDestinosCriticos,
    calidadRegistroCampo,
    salidasPorDiaSemana,
    antiguedadPendientes,
    pendienteMasAntiguaDias,
    campoTotalSolicitudes,
    campoConDestino,
    campoSinDestino,
    campoPendientes,
    campoAprobadas,
    campoRechazadas,
    campoDestinoPct,
    campoPendientesTotal,
    campoConHoraFin,
    campoSinHoraFin,
    campoHoraFinPct,
  };
}
